import { all, select, put, delay, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IKnmListFilterRaw } from 'APIPackage/KnmListModule/interfaces/IKnmListFilterRaw';
import {
  createKnmListByPlanFilter,
  deleteKnmFilter,
  fetchKnmListByPlanFilterById,
  fetchKnmListByPlanFilters,
  updateKnmListByPlanFilter,
} from 'APIPackage/KnmListModule/services/knmListApi';
import { IKnmIndexPage } from 'APIPackage/KnmModule/interfaces/IKnmIndexPage';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { IMspCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IMspCategorySelectOption';
import { IRiskCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IRiskCategorySelectOption';
import {
  getCatalogRiskCategoriesSelectOptions,
  getCatalogsMspCategoriesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import {
  dictionariesKnmKindsItemGet,
  dictionariesKnoSupervisionGet,
  dictionariesSubjectKindsItemGet,
  dictionariesSubjectSubKindsItemGet,
  dictionariesSubjectTypesItemGet,
  dictionariesSupervisionTypesItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import { IDictionaryKnmKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnmKindSelectOption';
import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import {
  getDictionariesAllEventTypesSelectOptions,
  getDictionariesAllKnmKindsSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { INTERNAL_SERVER_ERROR, SERVER_ERROR } from 'CorePackage/MainModule/constants/httpErrors';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { mapKnmListByPlanFilterToRaw } from 'KnmListByPlanPackage/KnmListByPlanFiltersModule/common/mapKnmListByPlanFilterToRaw';
import { mapRawToKnmListByPlanFilter } from 'KnmListByPlanPackage/KnmListByPlanFiltersModule/common/mapRawToKnmListByPlanFilter';
import { initialKnmListByPlanFilter } from 'KnmListByPlanPackage/KnmListByPlanFiltersModule/constants/initialKnmListByPlanFilter';
import { PlanActionsEnum } from 'PlanPackage/MainModule/enums/PlanActionsEnum';
import { KNM_UNLIST_ERROR } from 'CorePackage/MainModule/constants/notices';
import { knmDeleteSagaFunc } from 'KnmPackage/MainModule/sagas/knmDeleteSagaFunc';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { fetchKnmFindIndex } from 'APIPackage/KnmModule/services/knmListApi';
import {
  fetchKnmApprove,
  fetchKnmApproveNote,
  fetchKnmApproveRemarks,
} from 'APIPackage/KnmModule/services/knmActionsApi';
import { fetchErknmRemoveFromPlan } from 'APIPackage/KnmModule/services/knmPlanApi';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IKnmListByPlanFilter } from 'KnmListByPlanPackage/KnmListByPlanFiltersModule/interfaces/IKnmListByPlanFilter';

import {
  knmListByPlanApproveAction,
  knmListByPlanApproveRemarks,
  knmListByPlanCommentKnmAction,
  knmListByPlanDelete,
  knmListByPlanExcludeAction,
  knmListByPlanFilterCreate,
  knmListByPlanFilterGetData,
  knmListByPlanFilterGetMeta,
  knmListByPlanFilterRemove,
  knmListByPlanFilterSetCurrent,
  knmListByPlanFilterSetData,
  knmListByPlanFilterSetLoading,
  knmListByPlanFilterSetMeta,
  knmListByPlanFilterUpdate,
  knmListByPlanGetData,
  knmListByPlanSetData,
} from '../actions/knmListByPlanActions';
import { KnmListByPlanActionsEnum } from '../enums/KnmListByPlanActionsEnum';
import {
  getKnmListByPlanCurrent,
  getKnmListByPlanData,
  getKnmListByPlanFilters,
  getKnmListByPlanIsFilterOn,
  getKnmListByPlanKnoOrganizationDictVersionId,
  getKnmListByPlanKnoSupervisionUuid,
  getKnmListByPlanOrder,
  getKnmListByPlanPage,
  getKnmListByPlanPlanId,
  getKnmListByPlanSearchValue,
  getKnmListByPlanSubjectKindsSelectOptions,
  getKnmListByPlanSubjectSubKindsSelectOptions,
  getKnmListByPlanSubjectTypesSelectOptions,
  getKnmListByPlanSupervisionTypeDictVersionId,
  getKnmListByPlanSupervisionTypesSelectOptions,
  getKnmListByPlanTypeList,
} from '../selectors/knmListByPlanSelectors';
import { mapRawToKnmListByPlanData } from '../common/mapRawToKnmListByPlanData';
import { IKnmListByPlanFilterWithMeta } from '../interfaces/IKnmListByPlanFilterWithMeta';

const PAGE_SIZE = 50;
const KNM_LIST_REFRESH_DELAY = 1000;

const commonCatalogsNames: Array<CommonCatalogsNamesEnum> = [
  CommonCatalogsNamesEnum.RISK_CATEGORIES,
  CommonCatalogsNamesEnum.MSP_CATEGORIES,
];

function* initCommonCatalogsSaga() {
  yield all([...commonCatalogsNames.map(catalogName => put(catalogGet(catalogName)))]);
}

export function* watchKnmListByPlanSaga() {
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_INIT_PAGE, knmListByPlanInitPageSaga);
  yield takeEvery(
    [KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_GET_DATA, KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_SET_BASIC_FILTERS],
    fetchKnmListByPlanSaga,
  );
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_GET_DATA_NEXT_PAGE, fetchKnmListByPlanNextPageSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_DELETE, knmListByPlanDeleteSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_COMMENT_KNM, knmListByPlanCommentKnmSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_EXCLUDE, knmListByPlanExcludeSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_APPROVE, knmListByPlanApproveSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_FILTER_GET_META, fetchFiltersSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_FILTER_GET_DATA, fetchFilterByIdSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_FILTER_CREATE, createKnmListByPlanFilterSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_FILTER_REMOVE, removeKnmListByPlanFilterSaga);
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_FILTER_UPDATE, updateKnmListByPlanFilterSaga);
  yield takeEvery(PlanActionsEnum.PLAN_SET_PLAN, initDictionariesKnmSupervisionTypesSaga);
  yield takeEvery(
    [PlanActionsEnum.PLAN_SET_PLAN, KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_SET_SUPERVISION_TYPE_DICT_VERSION_ID],
    initDictionariesKnoSupervisionSaga,
  );
  yield takeEvery(DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_SUCCESS, initDictionariesSubjectTypesSaga);
  yield takeEvery(DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_SUCCESS, initDictionariesSubjectKindsSaga);
  yield takeEvery(
    DictionariesActionsEnum.DICTIONARIES_KNO_SUPERVISION_GET_SUCCESS,
    initDictionariesSubjectSubKindsSaga,
  );
  yield takeEvery(KnmListByPlanActionsEnum.KNM_LIST_BY_PLAN_APPROVE_REMARKS, knmListByPlanApproveRemarksSaga);
}

function* knmListByPlanInitPageSaga() {
  yield initCommonCatalogsSaga();
  yield put(knmListByPlanFilterGetMeta());
  yield put(dictionariesKnmKindsItemGet());
}

function getRawFilter(state: IRootState) {
  const planId = getKnmListByPlanPlanId(state);
  const typeList = getKnmListByPlanTypeList(state);

  if (!planId || !typeList) {
    return;
  }
  const current = getKnmListByPlanCurrent(state);
  const isFilterOn = getKnmListByPlanIsFilterOn(state);

  return {
    ...mapKnmListByPlanFilterToRaw((isFilterOn && current) || initialKnmListByPlanFilter),
    planNum: planId,
    typeList,
  };
}

function* fetchKnmListByPlanSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const searchValue = getKnmListByPlanSearchValue(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const order = getKnmListByPlanOrder(state);
    const rawFilter = getRawFilter(state);

    if (!rawFilter) {
      return;
    }
    const res: IKnmIndexPage = yield fetchKnmFindIndex(
      0,
      PAGE_SIZE,
      searchValue ? { ...rawFilter, searchString: searchValue } : rawFilter,
      order ?? '',
    );

    yield put(
      knmListByPlanSetData(mapRawToKnmListByPlanData(res.list, isErknm), res.page, res.pageCount, res.totalCount),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* fetchKnmListByPlanNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const searchValue = getKnmListByPlanSearchValue(state);
    const data = getKnmListByPlanData(state);
    const page = getKnmListByPlanPage(state);
    const order = getKnmListByPlanOrder(state);
    const rawFilter = getRawFilter(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

    if (!rawFilter) {
      return;
    }

    const res: IKnmIndexPage = yield fetchKnmFindIndex(
      page + 1,
      PAGE_SIZE,
      searchValue ? { ...rawFilter, searchString: searchValue } : rawFilter,
      order ?? '',
    );

    yield put(
      knmListByPlanSetData(
        [...data, ...mapRawToKnmListByPlanData(res.list, isErknm)],
        res.page,
        res.pageCount,
        res.totalCount,
      ),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* fetchFiltersSaga() {
  function* funcTry() {
    yield put(knmListByPlanFilterSetLoading(true));
    const meta: IFilterResult[] = yield fetchKnmListByPlanFilters();

    yield put(knmListByPlanFilterSetMeta(meta));
    const current: IKnmListByPlanFilter | undefined = yield select(getKnmListByPlanCurrent);
    const currentId = meta.find(m => m.name === current?.name)?.id;

    yield put(knmListByPlanFilterSetCurrent({ current, currentId }));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmListByPlanFilterSetLoading(false));
    },
  });
}

function* fetchFilterByIdSaga(action: ActionType<typeof knmListByPlanFilterGetData>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const filters = getKnmListByPlanFilters(state);
    const riskCategoriesSelectOptions: IRiskCategorySelectOption[] = getCatalogRiskCategoriesSelectOptions(state);
    const mspCategoriesSelectOptions: IMspCategorySelectOption[] = getCatalogsMspCategoriesSelectOptions(state);
    const supervisionTypesSelectOptions:
      | IDictionarySupervisionTypeSelectOption[]
      | undefined = getKnmListByPlanSupervisionTypesSelectOptions(state);
    const objectTypesSelectOptions:
      | IDictionarySubjectTypeSelectOption[]
      | undefined = getKnmListByPlanSubjectTypesSelectOptions(state);
    const objectKindsSelectOptions:
      | IDictionarySubjectKindSelectOption[]
      | undefined = getKnmListByPlanSubjectKindsSelectOptions(state);
    const objectSubKindsSelectOptions:
      | IDictionarySubjectSubKindSelectOption[]
      | undefined = getKnmListByPlanSubjectSubKindsSelectOptions(state);
    const knmKindsSelectOptions: IDictionaryKnmKindSelectOption[] | undefined = getDictionariesAllKnmKindsSelectOptions(
      state,
    );
    const eventTypesSelectOptions:
      | IDictionaryEventTypeSelectOption[]
      | undefined = getDictionariesAllEventTypesSelectOptions(state);

    if (filters.find(f => f.meta.id === id)?.data !== undefined) {
      return;
    }

    yield put(knmListByPlanFilterSetLoading(true));
    const raw: IKnmListFilterRaw = yield fetchKnmListByPlanFilterById(id);
    const filter = mapRawToKnmListByPlanFilter(
      raw,
      riskCategoriesSelectOptions,
      mspCategoriesSelectOptions,
      supervisionTypesSelectOptions,
      objectTypesSelectOptions,
      objectKindsSelectOptions,
      objectSubKindsSelectOptions,
      knmKindsSelectOptions,
      eventTypesSelectOptions,
    );

    yield put(knmListByPlanFilterSetData(id, filter));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmListByPlanFilterSetLoading(false));
    },
  });
}

function* createKnmListByPlanFilterSaga(action: ActionType<typeof knmListByPlanFilterCreate>) {
  function* funcTry() {
    const { data } = action.payload;

    yield put(knmListByPlanFilterSetLoading(true));
    const rawFilter = mapKnmListByPlanFilterToRaw(data);
    const meta: IFilterResult = yield createKnmListByPlanFilter(rawFilter);
    const filters: IKnmListByPlanFilterWithMeta[] = yield select(getKnmListByPlanFilters);
    const metaList = filters.map(filter => filter.meta);

    yield put(knmListByPlanFilterSetMeta([...metaList, meta]));
    yield put(knmListByPlanFilterSetData(meta.id, data));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmListByPlanFilterSetLoading(false));
    },
  });
}

function* removeKnmListByPlanFilterSaga(action: ActionType<typeof knmListByPlanFilterRemove>) {
  function* funcTry() {
    const { id } = action.payload;

    yield put(knmListByPlanFilterSetLoading(true));
    yield deleteKnmFilter(id);
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmListByPlanFilterSetLoading(false));
    },
  });
}

function* updateKnmListByPlanFilterSaga(action: ActionType<typeof knmListByPlanFilterUpdate>) {
  function* funcTry() {
    const { id, data } = action.payload;

    yield put(knmListByPlanFilterSetLoading(true));
    const rawFilter = mapKnmListByPlanFilterToRaw(data);

    yield updateKnmListByPlanFilter(id, rawFilter);
    yield put(knmListByPlanFilterSetData(id, data));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmListByPlanFilterSetLoading(false));
    },
  });
}

function* initDictionariesKnmSupervisionTypesSaga() {
  const state: IRootState = yield select();
  const knoOrganizationDictVersionId = state.plan.data?.knmPlanData?.knoOrganizationErknm?.dictVersionId;

  if (knoOrganizationDictVersionId) {
    yield put(dictionariesSupervisionTypesItemGet([knoOrganizationDictVersionId], true));
  }
}

function* initDictionariesKnoSupervisionSaga() {
  const state: IRootState = yield select();
  const knoOrganizationDictVersionId = getKnmListByPlanKnoOrganizationDictVersionId(state);
  const supervisionTypeDictVersionId = getKnmListByPlanSupervisionTypeDictVersionId(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    yield put(dictionariesKnoSupervisionGet(knoOrganizationDictVersionId, supervisionTypeDictVersionId));
  }
}

function* initDictionariesSubjectTypesSaga() {
  const knoSupervisionUuid: string | undefined = yield select(getKnmListByPlanKnoSupervisionUuid);

  if (knoSupervisionUuid) {
    yield put(dictionariesSubjectTypesItemGet(knoSupervisionUuid));
  }
}

function* initDictionariesSubjectKindsSaga() {
  const knoSupervisionUuid: string | undefined = yield select(getKnmListByPlanKnoSupervisionUuid);

  if (knoSupervisionUuid) {
    yield put(dictionariesSubjectKindsItemGet(knoSupervisionUuid));
  }
}

function* initDictionariesSubjectSubKindsSaga() {
  const knoSupervisionUuid: string | undefined = yield select(getKnmListByPlanKnoSupervisionUuid);

  if (knoSupervisionUuid) {
    yield put(dictionariesSubjectSubKindsItemGet(knoSupervisionUuid));
  }
}

function* knmListByPlanCommentKnmSaga(action: ActionType<typeof knmListByPlanCommentKnmAction>) {
  function* funcTry() {
    const { knmIds, note } = action.payload;

    if (note) {
      yield fetchKnmApproveNote(knmIds, note);
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.COMMENT_KNM,
          renderContent: 'Замечание успешно добавлено',
        }),
      );
      yield delay(KNM_LIST_REFRESH_DELAY);
      yield fetchKnmListByPlanSaga();
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.COMMENT_KNM,
          renderContent: SERVER_ERROR,
        }),
      );
    },
  });
}

function* knmListByPlanExcludeSaga(action: ActionType<typeof knmListByPlanExcludeAction>) {
  function* funcTry() {
    const { ids, params, note } = action.payload;

    if (note) {
      yield fetchKnmApproveNote(
        ids.map(item => +item),
        note,
      );
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.COMMENT_KNM,
          renderContent: 'Замечание успешно добавлено',
        }),
      );
    }
    if (params) {
      const { reason, document, signatureInfo, type, linkedPmId, requisites } = params;

      yield fetchErknmRemoveFromPlan(
        ids,
        reason,
        {
          active: null,
          attachments: [
            {
              ...document,
              signatureInfo,
              signed: true,
            },
          ],
          correlationToken: null,
          description: null,
          guid: null,
          type,
        },
        linkedPmId,
        requisites,
      );
    } else {
      yield fetchErknmRemoveFromPlan(ids);
    }
    yield delay(KNM_LIST_REFRESH_DELAY);
    yield fetchKnmListByPlanSaga();
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.EXCLUDE_KNM,
        renderContent: 'КНМ успешно исключены',
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: KNM_UNLIST_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.UNLIST_KNM,
        }),
      );
    },
  });
}

function* knmListByPlanApproveSaga(action: ActionType<typeof knmListByPlanApproveAction>) {
  function* funcTry() {
    const { knmIds } = action.payload;

    yield fetchKnmApprove(knmIds);
    yield delay(KNM_LIST_REFRESH_DELAY);
    yield fetchKnmListByPlanSaga();
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.EXCLUDE_KNM,
        renderContent: 'КНМ успешно согласованы',
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.EXCLUDE_KNM,
          renderContent: SERVER_ERROR,
        }),
      );
    },
  });
}

function* knmListByPlanDeleteSaga(action: ActionType<typeof knmListByPlanDelete>) {
  function* funcTry() {
    const { ids } = action.payload;

    yield knmDeleteSagaFunc(ids);
    yield put(knmListByPlanGetData());
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmListByPlanApproveRemarksSaga(action: ActionType<typeof knmListByPlanApproveRemarks>) {
  function* funcTry() {
    const { knmIds, remarks, note } = action.payload;

    yield fetchKnmApproveRemarks(knmIds, remarks, note);
  }

  function* funcCatch() {
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.COMMENT_KNM,
        renderContent: INTERNAL_SERVER_ERROR,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}
